import { FileUpload } from "@/components/ui/file-upload";
import { postFormAuthorized } from "@/lib/fetch";
import { Button } from "@heroui/react";
import { useState, useEffect } from "react";
import { Control, useForm, FormProvider } from "react-hook-form";
import { UploadSchemaType } from "./filter";
import { AutocompleteAsyncField } from "@/features/FormCard/Fields";
import { Form } from "@/components/ui/form";
import { useTranslation } from "react-i18next";

type Mode = "create" | "modify";

type UploadFormValues = UploadSchemaType & {
  config_id?: number | null;
  config_name?: { text: string } | null;
};

type UploadNewProps = {
  setIsOpen: (val: boolean) => void;
  files: File[];
  setFiles: (files: File[]) => void;
  selectedConfig: string | null;
  setSelectedConfig: (config: string | null) => void;
  selectedConfigId: number | null;
  setSelectedConfigId: (id: number | null) => void;
  mode?: Mode;
  fileId?: number;
  onSuccess?: () => Promise<void> | void;
};

export function UploadNew({
  setIsOpen,
  files,
  setFiles,
  selectedConfig,
  setSelectedConfig,
  selectedConfigId,
  setSelectedConfigId,
  mode = "create",
  onSuccess,
}: UploadNewProps) {
  const { t } = useTranslation(["common", "tables"]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<UploadFormValues>({
    defaultValues: {
      config_name: selectedConfig ? { text: selectedConfig } : undefined,
      config_id: selectedConfigId,
    },
    shouldUnregister: false,
  });

  const { control, watch } = form;
  const controlForAutocomplete =
    control as unknown as Control<UploadFormValues>;

  const watchedConfig = watch("config_name");
  const currentId = watch("config_id");
  useEffect(() => {
    if (watchedConfig?.text !== selectedConfig) {
      setSelectedConfig(watchedConfig?.text ?? null);
    }
  }, [watchedConfig, selectedConfig, setSelectedConfig]);

  useEffect(() => {
    if (currentId !== selectedConfigId) {
      setSelectedConfigId(currentId ?? null);
    }
  }, [currentId, selectedConfigId, setSelectedConfigId]);

  const handleUpload = async (url: string) => {
    if (!files.length || files.length !== 1) {
      console.error("Exactly one file must be selected");
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("file", files[0]); // backend expects `file`
    if (currentId !== null && currentId !== undefined) {
      formData.append("provider_config_id", String(currentId));
    }

    try {
      await postFormAuthorized(url, formData);
      await onSuccess?.();
      setIsOpen(false);
      setFiles([]);
    } catch (err) {
      console.error("Upload error", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    handleUpload(
      `${import.meta.env.VITE_BACKEND_URL}/etl-quantilia/time_serie_files/upload`,
    );
  };

  const handleReset = () => {
    setIsOpen(false);
    setFiles([]);
  };

  return (
    <div className="flex justify-center items-start w-full px-4 py-6">
      <FormProvider {...form}>
        <Form {...form}>
          <div className="flex flex-col w-full max-w-card rounded-lg p-6 gap-4 bg-white">
            {mode === "create" && (
              <AutocompleteAsyncField
                name="config_name"
                label={t("time_serie_files.columns.provider_config_code", {
                  ns: "tables",
                  defaultValue: "Configuration",
                })}
                base_url="etl-quantilia/provider_configs"
                column_name="code"
                id_column="config_id"
                control={controlForAutocomplete}
              />
            )}

            <FileUpload
              files={files}
              setFiles={setFiles}
              setError={() => {}}
              maxFiles={1} // enforce single file
              hideStatusChips={true}
            />

            <footer className="flex justify-end gap-2">
              <Button
                color="primary"
                radius="full"
                size="sm"
                onPress={handleSubmit}
                disabled={
                  isSubmitting || currentId == null || files.length != 1
                }
                className="disabled:opacity-50"
              >
                {isSubmitting ? t("actions.uploading") : t("actions.upload")}
              </Button>
              <Button
                color="primary"
                radius="full"
                variant="flat"
                size="sm"
                onPress={handleReset}
              >
                {t("actions.cancel")}
              </Button>
            </footer>
          </div>
        </Form>
      </FormProvider>
    </div>
  );
}
